/**
 * DEV-009 · One-Tap Directions — web helper.
 *
 * Builds a URL to hand off navigation from SolarFlow to the user's default
 * mapping provider (Apple Maps on macOS/iPadOS Safari, Google Maps on
 * everything else). Uses the public "search" URL that requires NO API key
 * and NO SDK, so DEV-009 stays within its "no Google Maps API" constraint.
 *
 * Rule: SolarFlow only passes the destination address. No customer name,
 * no email, no phone, no coordinates — see DEV-009 privacy constraint.
 */

/**
 * Prefer the visit's own `location` (rare — for offsite pickups like
 * warehouse deliveries) then the appointment's denormalised
 * `project_address`, then the parent project's `address`.
 * Returns a trimmed non-empty string, or null when SolarFlow has no
 * usable destination.
 */
export function resolveMapAddress({ visit = null, project = null } = {}) {
    const candidates = [
        visit?.location,
        visit?.project_address,
        project?.address,
    ];
    for (const c of candidates) {
        if (typeof c === "string" && c.trim()) return c.trim();
    }
    return null;
}

/**
 * Cross-platform maps URL. Uses `google.com/maps/dir/?api=1` — the public
 * search URL, not the JavaScript / Places API. Works in every browser and
 * hands off to native Apple/Google Maps on iOS/iPadOS/Android via the OS.
 */
export function buildMapsUrl(address) {
    if (!address) return null;
    const q = encodeURIComponent(address);
    return `https://www.google.com/maps/dir/?api=1&destination=${q}`;
}

/**
 * One-shot open helper — opens directions in a new tab so the SolarFlow
 * workflow is preserved. No-op (returns false) when address is empty.
 */
export function openDirections(address) {
    const url = buildMapsUrl(address);
    if (!url) return false;
    // noopener + noreferrer to prevent the new tab from getting a handle
    // back to SolarFlow (Rel Noopener Reverse Tabnabbing safe-default).
    window.open(url, "_blank", "noopener,noreferrer");
    return true;
}
